import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockFormSubmission, countryCodes } from '../utils/mock';
import { toast } from 'sonner';
import { X } from 'lucide-react';

export const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await mockFormSubmission({
        ...formData,
        fullPhone: `${formData.countryCode} ${formData.phone}`
      });
      
      if (response.success) {
        toast.success(response.message);
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          phone: '',
          message: ''
        });
        setErrors({});
        onClose();
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7C2D12] to-[#F97316] p-6 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Get Started Today</DialogTitle>
            <DialogDescription className="text-white/90">
              Fill out the form below and our trademark experts will contact you within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name Field */}
          <div>
            <Label htmlFor="name" className="text-[#7C2D12] font-semibold">
              Full Name *
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your full name"
              className={`mt-1 border-2 ${errors.name ? 'border-red-500' : 'border-[#FED7AA]'} focus:border-[#F97316]`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <Label htmlFor="email" className="text-[#7C2D12] font-semibold">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@example.com"
              className={`mt-1 border-2 ${errors.email ? 'border-red-500' : 'border-[#FED7AA]'} focus:border-[#F97316]`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <Label htmlFor="phone" className="text-[#7C2D12] font-semibold">
              Contact Number *
            </Label>
            <div className="flex gap-2 mt-1">
              <Select 
                value={formData.countryCode} 
                onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
              >
                <SelectTrigger className="w-32 border-2 border-[#FED7AA] focus:border-[#F97316]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((cc) => (
                    <SelectItem key={cc.code} value={cc.code}>
                      {cc.code} {cc.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="1234567890"
                maxLength={10}
                className={`flex-1 border-2 ${errors.phone ? 'border-red-500' : 'border-[#FED7AA]'} focus:border-[#F97316]`}
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <Label htmlFor="message" className="text-[#7C2D12] font-semibold">
              Message *
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your trademark needs..."
              rows={4}
              className={`mt-1 border-2 ${errors.message ? 'border-red-500' : 'border-[#FED7AA]'} focus:border-[#F97316] resize-none`}
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1">{errors.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#F97316] hover:bg-[#7C2D12] text-white font-bold py-6 text-lg transition-all duration-300"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            By submitting this form, you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPopup;
